import { useCallback, useEffect, useRef, useState } from "react";
import { Bell, CheckCheck, Coins, Clock, Rocket, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { api } from "../../lib/api";
import { usePanelT } from "../../hooks/usePanelT";

const POLL_MS = 15000;

function iconFor(n) {
    const t = (n.title || "").toLowerCase();
    if (t.includes("detected") || t.includes("confirming")) return <Clock className="w-4 h-4 text-yellow-300" />;
    if (t.includes("credited") || t.includes("confirmed") || t.includes("top-up")) return <Coins className="w-4 h-4 text-emerald-400" />;
    if (t.includes("refund") || t.includes("cancel")) return <Undo2 className="w-4 h-4 text-sky-300" />;
    return <Rocket className="w-4 h-4 text-primary" />;
}

export default function NotificationBell({ onUnreadChange, testId = "panel-bell" }) {
    const { t } = usePanelT();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const seen = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/me/notifications");
            setItems(data);
            onUnreadChange?.(data.filter((n) => !n.read).length);
            if (seen.current) {
                data.filter((n) => !seen.current.has(n.id) && !n.read).forEach((n) => {
                    toast(n.title, { description: n.body, duration: 8000, icon: iconFor(n) });
                });
            }
            seen.current = new Set(data.map((n) => n.id));
        } catch { /* ignore */ }
    }, [onUnreadChange]);

    useEffect(() => {
        load();
        const id = setInterval(load, POLL_MS);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
    }, [load]);

    const unread = items.filter((n) => !n.read);

    const markAllRead = async () => {
        await Promise.all(unread.map((n) => api.post(`/me/notifications/${n.id}/read`).catch(() => {})));
        load();
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={testId} aria-label="Notifications" className="relative w-10 h-10 grid place-items-center rounded-xl border border-white/10 bg-white/[0.02] hover:bg-white/[0.06] transition-colors">
                    <Bell className="w-4 h-4" />
                    {unread.length > 0 && (
                        <span data-testid={`${testId}-count`} className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold grid place-items-center">{unread.length > 9 ? "9+" : unread.length}</span>
                    )}
                </button>
            </PopoverTrigger>
            <PopoverContent align="end" data-testid="panel-bell-panel" className="w-[360px] max-w-[calc(100vw-2rem)] p-0 bg-[#0d0d10] border-white/10">
                <div className="px-4 py-3 border-b border-white/[0.06] flex items-center justify-between">
                    <p className="font-display font-semibold text-sm">{t("nb.title")}</p>
                    {unread.length > 0 && (
                        <button type="button" onClick={markAllRead} data-testid="panel-bell-mark-read" className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"><CheckCheck className="w-3.5 h-3.5" /> {t("nb.markRead")}</button>
                    )}
                </div>
                <ul className="max-h-[380px] overflow-y-auto divide-y divide-white/[0.05]">
                    {items.length === 0 && <li className="px-4 py-8 text-center text-sm text-muted-foreground">{t("nb.empty")}</li>}
                    {items.slice(0, 30).map((n) => (
                        <li key={n.id} data-testid={`notification-${n.id}`} data-read={n.read} className={`px-4 py-3 flex gap-3 ${n.read ? "opacity-70" : "bg-primary/[0.04]"}`}>
                            <span className="mt-0.5 shrink-0">{iconFor(n)}</span>
                            <div className="min-w-0">
                                <p className={`text-sm leading-tight ${n.read ? "" : "font-semibold"}`}>{n.title}</p>
                                <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.body}</p>
                                <p className="text-[10px] font-mono text-muted-foreground/70 mt-1">{new Date(n.created_at).toLocaleString()}</p>
                            </div>
                        </li>
                    ))}
                </ul>
            </PopoverContent>
        </Popover>
    );
}
